import React from "react";
import {
  TrendingUp,
  Rocket,
  Leaf,
  Smartphone,
  BookOpen,
  Target,
} from "lucide-react";
import { motion } from "framer-motion";

const reasons = [
  {
    icon: <TrendingUp className="text-white" size={32} />,
    title: "Proven Subscription Model",
    description: "Steady, recurring revenue from a loyal and growing reader base.",
  },
  {
    icon: <Rocket className="text-white" size={32} />,
    title: "Scalable Logistics & Tech",
    description: "Built for rapid expansion across cities and rural areas.",
  },
  {
    icon: <Leaf className="text-white" size={32} />,
    title: "High Social Impact",
    description: "Boost literacy and promote sustainable consumption nationwide.",
  },
  {
    icon: <Smartphone className="text-white" size={32} />,
    title: "AI-Driven Platform",
    description: "Smart book recommendations, behavior tracking, and insights.",
  },
  {
    icon: <BookOpen className="text-white" size={32} />,
    title: "Huge Market Opportunity",
    description: "Low competition in Tier 2/3 cities and underserved areas.",
  },
  {
    icon: <Target className="text-white" size={32} />,
    title: "Strong Local Traction",
    description: "Thousands of happy customers and rapid organic growth.",
  },
];

// Helper function to calculate position for circular arrangement
const calculateCircularPosition = (index, total, radiusPercent) => {
  // Using the requested offset for visual centering
  const CENTER_OFFSET = -13.0; 
  
  // Start at the top (-90 degrees) and distribute the items evenly
  const angle = (360 / total) * index - 90; 
  const rad = (angle * Math.PI) / 180;

  // Calculate coordinates relative to the center (50%, 50%)
  const x = radiusPercent * Math.cos(rad);
  const y = radiusPercent * Math.sin(rad);

  // Position is calculated from the top-left corner of the item.
  // We offset by 50% to start from the container center, then add the x/y offset, plus the correction.
  const left = 50 + x + CENTER_OFFSET; 
  const top = 50 + y + CENTER_OFFSET; 

  return {
    left: `${left}%`,
    top: `${top}%`,
  };
};

// Component for the central pulsating sphere, now with the "Reasons" text
const CentralSphere = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    {/* The central circle, which holds the text and the pulsating background */}
    <div className="relative w-1/3 h-1/3 bg-primary/20 rounded-full flex items-center justify-center overflow-hidden">
      
      {/* Innermost pulsating background circle (covers the entire parent circle) */}
      {/* Updated class to use the new heartbeat animation */}
      <div className="absolute w-full h-full bg-primary/30 rounded-full animate-heartbeat">
      </div>

      {/* Text layer, placed above the pulsating background */}
      <span className="text-xl md:text-2xl font-extrabold text-slate-900 tracking-wider z-10">
        Reasons
      </span>
    </div>
  </div>
);

const WhyInvestSection = () => {
  const totalReasons = reasons.length;
  
  // Item size as a percentage of container size (30% diameter, increased from 25%)
  const itemSizePercent = 30; 
  
  // Radius of the circle on which the items' centers will sit (adjusted for new size)
  const radiusPercent = 40; 

  return (
    <section
      id="why-invest"
      className="relative py-20 px-6 md:px-12 bg-gradient-to-b from-white via-[#FFFAF8] to-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto text-center">
        <motion.h2
          className="text-4xl md:text-4xl font-bold text-slate-900 mb-6"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Why Invest in <span className="text-primary">BooksAtDoorsteps.com</span>?
        </motion.h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-14">
          We’re not just delivering books — we’re building a reading revolution across India. Here’s why joining us makes sense.
        </p>

        {/* Circular Layout Container (must be a square for math to work and relative for absolute children) */}
        <div className="relative w-full max-w-md md:max-w-lg lg:max-w-4xl aspect-square mx-auto mt-2 mb-16">
          
          <CentralSphere />
          
          {reasons.map((reason, idx) => {
            const positionStyle = calculateCircularPosition(idx, totalReasons, radiusPercent);
            
            return (
              <motion.div
                key={idx}
                // ADDED 'group', increased shadow to 'shadow-2xl', ADDED hover styles
                className="absolute group p-4 border border-primary/20 bg-white/70 backdrop-blur-sm shadow-2xl rounded-full flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 transform hover:bg-primary hover:border-primary"
                style={{
                  ...positionStyle,
                  width: `${itemSizePercent}%`,
                  height: `${itemSizePercent}%`,
                  transform: 'translate(-50%, -50%)', // Centering the item based on the calculated (left, top) point
                }}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                viewport={{ once: true }}
              >
                {/* Icon wrapper: added group-hover:bg-white/30 for contrast */}
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary shadow-lg mb-3 flex-shrink-0 transition-colors duration-300 group-hover:bg-white/30">
                  {/* Icon size is 28 (Icon remains white as defined in the 'reasons' array) */}
                  {React.cloneElement(reason.icon, { size: 28 })} 
                </div>
                
                {/* Heading: added group-hover:text-white */}
                <h3 className="text-base md:text-lg font-bold text-slate-800 leading-tight mb-1 overflow-hidden text-ellipsis group-hover:text-white transition-colors duration-300">
                  {reason.title}
                </h3>
                {/* Description: added group-hover:text-white/90 */}
                <p className="hidden lg:block text-xs text-gray-600 leading-snug group-hover:text-white/90 transition-colors duration-300">
                  {reason.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
      <style>{`
        /* Custom animation for the central pulsating sphere */
        @keyframes heartbeat {
            0% { transform: scale(1); }
            10% { transform: scale(1.05); }
            20% { transform: scale(1); }
            30% { transform: scale(1.03); }
            40% { transform: scale(1); }
            100% { transform: scale(1); }
        }
        .animate-heartbeat {
            animation: heartbeat 0.8s ease-in-out infinite; 
        }

        /* Removed old pulse-slow keyframes */
      `}</style>
    </section>
  );
};

export default WhyInvestSection;
